import React from 'react';
import { X } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { ChipState, ScheduleRow } from '../../types/schedule';
import { computeEntryChip, computeExitChip } from '../../utils/scheduleChip';
import { STATUS_VARIANT_CLASSES } from '../../utils/statusColors';
import { formatPnlParts as formatPnl, formatPrice } from '../../utils/positionDisplay';
import { Modal } from '../ui/Modal';
import BrokerBreakdownLine from './shared/BrokerBreakdownLine';

interface SchedulePreviewModalProps {
  basketName: string;
  strategyName: string;
  rows: ScheduleRow[];
  isToday: boolean;
  isPast: boolean;
  currentIstHms: string | null;
  onClose: () => void;
}

const CHIP_CLASSES = STATUS_VARIANT_CLASSES;

function Chip({ state }: { state: ChipState | null }) {
  if (!state) return <span className="text-gray-300 dark:text-gray-600">—</span>;
  return (
    <span
      title={state.subtext || undefined}
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap',
        CHIP_CLASSES[state.variant],
      )}
    >
      {state.label}
    </span>
  );
}


/**
 * Schedule preview popup — scoped to one strategy's legs on a given date.
 * Shows scheduled entry/exit times, chip-driven status, and (when available)
 * live fill prices and P&L overlaid from the actual Position record.
 *
 * Opened by clicking a Basket · Strategy link in the ScheduleTable.
 */
const SchedulePreviewModal: React.FC<SchedulePreviewModalProps> = ({
  basketName,
  strategyName,
  rows,
  isToday,
  isPast,
  currentIstHms,
  onClose,
}) => {
  const chipCtx = { isToday, isPast, nowIstHms: currentIstHms };

  const totalPnl = rows.reduce((s, r) => s + (r.pnl ?? 0), 0);
  const pnl = formatPnl(totalPnl);
  // Dedup by leg_id when computing the header: `_build_rows_for_strategy`
  // emits one row per (leg × matching position), so a leg with 2 broker
  // positions appears as 2 rows. The header should show the configured leg
  // count and the effective lots that will execute, not row counts.
  const seenLegIds = new Set<string>();
  let effectiveLots = 0;
  for (const r of rows) {
    if (seenLegIds.has(r.leg.leg_id)) continue;
    seenLegIds.add(r.leg.leg_id);
    const breakdown = r.leg_broker_breakdown ?? [];
    if (breakdown.length === 0) {
      effectiveLots += r.leg.lots ?? 0;
    } else {
      for (const b of breakdown) effectiveLots += b.effective_lots;
    }
  }
  const legCount = seenLegIds.size;
  const totalLots = effectiveLots;
  const tradingType = rows[0]?.trading_type || '';
  const underlying = rows[0]?.underlying || '';

  return (
    <Modal
      isOpen
      onClose={onClose}
      maxWidth="5xl"
      ariaLabel={`${strategyName} schedule preview`}
    >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400 truncate">
              {basketName || '—'}
            </div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">
                {strategyName || '—'}
              </h3>
              <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                ({legCount} {legCount === 1 ? 'leg' : 'legs'} · {totalLots}L)
              </span>
              {underlying && (
                <span className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {underlying}
                </span>
              )}
              {tradingType && (
                <span className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  · {tradingType}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Total P&amp;L</div>
              <div className={cn('text-sm font-bold tabular-nums', pnl.className)}>{pnl.text}</div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded-md text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto">
          {rows.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
              No legs scheduled for this strategy on this day.
            </div>
          ) : (
            <table className="min-w-full">
              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  {['Leg', 'Entry Time', 'Entry', 'Entry Px', 'Exit Time', 'Exit', 'Exit Px', 'P&L', 'SL'].map((h) => (
                    <th
                      key={h}
                      className="px-3 py-2.5 text-left text-[10px] font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const entryChip = computeEntryChip(r, chipCtx);
                  const exitChip = computeExitChip(r, chipCtx);
                  const pnlDisp = formatPnl(r.pnl);
                  return (
                    <tr key={r.row_key} className="border-b border-gray-100 dark:border-gray-700/40 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-3 py-2.5 text-xs text-gray-700 dark:text-gray-200 whitespace-nowrap">
                        <div className="flex flex-col gap-0.5">
                          <span>
                            <span className="font-mono">#{r.leg.leg_index}</span>{' '}
                            <span className={cn('font-medium', r.leg.option_type === 'CE' ? 'text-blue-600 dark:text-blue-400' : 'text-purple-600 dark:text-purple-400')}>
                              {r.leg.option_type}
                            </span>{' '}
                            <span className={cn('font-medium', r.leg.action === 'BUY' ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400')}>
                              {r.leg.action}
                            </span>{' '}
                            <span className="text-gray-500 dark:text-gray-400">{r.leg.lots}L</span>
                          </span>
                          <BrokerBreakdownLine items={r.leg_broker_breakdown ?? []} />
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300 tabular-nums whitespace-nowrap">
                        {r.scheduled_entry_time || '—'}
                      </td>
                      <td className="px-3 py-2.5"><Chip state={entryChip} /></td>
                      <td className="px-3 py-2.5 text-xs text-gray-700 dark:text-gray-200 tabular-nums whitespace-nowrap">
                        {formatPrice(r.average_entry_price ?? r.expected_entry_price)}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300 tabular-nums whitespace-nowrap">
                        {r.scheduled_exit_time || '—'}
                      </td>
                      <td className="px-3 py-2.5"><Chip state={exitChip} /></td>
                      <td className="px-3 py-2.5 text-xs text-gray-700 dark:text-gray-200 tabular-nums whitespace-nowrap">
                        {formatPrice(r.exit_average_price ?? r.expected_exit_price)}
                      </td>
                      <td className={cn('px-3 py-2.5 text-xs font-medium tabular-nums whitespace-nowrap', pnlDisp.className)}>
                        {pnlDisp.text}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {r.sl_trigger_price != null ? (
                          <span className="tabular-nums text-amber-700 dark:text-amber-300">
                            {formatPrice(r.sl_trigger_price)}
                          </span>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
    </Modal>
  );
};

export default SchedulePreviewModal;